import React, { useState, useRef } from 'react';
import JSZip from 'jszip';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faFileArrowUp, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faFileArrowUp, faXmark);

// Default accepted types — extend this array to support additional file types.
// Each entry describes how to handle a given extension.
// `extractable: true` means the file acts as an archive; its contents are extracted.
const DEFAULT_ACCEPTED_TYPES = [
  { ext: 'xml',  label: 'XML',  mime: ['text/xml', 'application/xml'] },
  { ext: 'xaf',  label: 'XAF',  mime: ['application/octet-stream', ''] },
  { ext: 'zip',  label: 'ZIP',  mime: ['application/zip', 'application/x-zip-compressed'], extractable: true },
];

const FileDropComponent = ({ onFilesAccepted, acceptedTypes = DEFAULT_ACCEPTED_TYPES }) => {
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const acceptedExts = acceptedTypes.map(t => t.ext.toLowerCase());
  const extractableExts = acceptedTypes.filter(t => t.extractable).map(t => t.ext.toLowerCase());

  function getExtension(filename) {
    return filename.split('.').pop().toLowerCase();
  }

  function isAccepted(filename) {
    return acceptedExts.includes(getExtension(filename));
  }

  async function extractZip(file) {
    const zip = await JSZip.loadAsync(file);
    const extracted = [];
    const promises = [];

    zip.forEach((relativePath, zipEntry) => {
      if (!zipEntry.dir && isAccepted(zipEntry.name)) {
        const promise = zipEntry.async('blob').then(blob => {
          const ext = getExtension(zipEntry.name);
          const mimeEntry = acceptedTypes.find(t => t.ext === ext);
          const mime = mimeEntry?.mime?.[0] || '';
          extracted.push(new File([blob], zipEntry.name, { type: mime }));
        });
        promises.push(promise);
      }
    });

    await Promise.all(promises);
    return extracted;
  }

  async function processFileList(fileList) {
    setError(null);
    const incoming = Array.from(fileList);
    const valid = incoming.filter(f => isAccepted(f.name));
    const invalid = incoming.filter(f => !isAccepted(f.name));

    if (invalid.length > 0) {
      setError(`Unsupported file type(s): ${invalid.map(f => f.name).join(', ')}`);
    }

    const resolved = [];

    for (const file of valid) {
      const ext = getExtension(file.name);
      if (extractableExts.includes(ext)) {
        const extracted = await extractZip(file);
        if (extracted.length === 0) {
          setError(`No supported files found inside ${file.name}`);
        }
        resolved.push(...extracted);
      } else {
        resolved.push(file);
      }
    }

    if (resolved.length === 0) return;

    const updated = [...files, ...resolved];
    setFiles(updated);
    onFilesAccepted?.(updated);
  }

  function handleDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setDragging(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    processFileList(e.dataTransfer.files);
  }

  function handleInputChange(e) {
    processFileList(e.target.files);
    e.target.value = '';
  }

  function removeFile(index) {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    onFilesAccepted?.(updated);
  }

  const acceptAttr = acceptedTypes
    .flatMap(t => [`.${t.ext}`, ...(t.mime || [])])
    .filter(Boolean)
    .join(',');

  const labelList = acceptedTypes.map(t => t.label).join(', ');

  return (
    <div className="file-drop-wrapper">
      <div
        className={`file-drop-zone${dragging ? ' file-drop-zone--active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptAttr}
          className="file-drop-input"
          onChange={handleInputChange}
        />
        <FontAwesomeIcon icon={faFileArrowUp} className="file-drop-icon" />
        <p className="file-drop-label">
          Drag &amp; drop files here, or <span className="file-drop-browse">browse</span>
        </p>
        <p className="file-drop-hint">Supported: {labelList}</p>
      </div>

      {error && <p className="file-drop-error">{error}</p>}

      {files.length > 0 && (
        <ul className="file-drop-list">
          {files.map((file, i) => (
            <li key={i} className="file-drop-item">
              <span className="file-drop-item-name">{file.name}</span>
              <button
                className="file-drop-item-remove"
                onClick={() => removeFile(i)}
                type="button"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileDropComponent;
